import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';

/**
 * Datenschutzerklärung (DSGVO / RGPD).
 *
 * Die Praxis hat ihren Sitz in Spanien – es gelten die DSGVO (Verordnung (EU)
 * 2016/679) sowie die spanischen Datenschutzgesetze (LOPDGDD). Inhalt liegt
 * sprachweise inline als strukturierte Daten vor (kein fragiler Übersetzungs-
 * Lookup), damit die Seite nie crasht und beim Prerendering vollständigen Text
 * für Suchmaschinen liefert.
 *
 * Hinweis: Diese Erklärung ist eine sorgfältig erstellte, branchenübliche
 * Vorlage. Konkrete Angaben (z. B. Auftragsverarbeiter, Aufbewahrungsfristen)
 * sollten vor Produktivschaltung anwaltlich bzw. durch den Datenschutz-
 * beauftragten geprüft werden.
 */

// ── Stammdaten ────────────────────────────────────────────────────────────────
const OWNER = 'Jan D. Heiringhoff';
const PROVIDER = 'SeaDent Mallorca';
const ADDRESS = 'Avenida de Jaime III, № 3, 07012 Palma de Mallorca, Illes Balears, España';
const PHONE = '+34 603 31 96 48';
const EMAIL = 'info@seadent.es';
const LAST_UPDATED = 'Juni 2026';

type Lang = 'de' | 'en' | 'es' | 'ru';

interface Section {
  heading: string;
  paragraphs?: string[];
  bulletsIntro?: string;
  bullets?: string[];
}

interface PrivacyDoc {
  title: string;
  intro: string;
  controllerHeading: string;
  controllerIntro: string;
  sections: Section[];
  status: string;
}

const CONTACT_BLOCK = `${PROVIDER} · ${OWNER} · ${ADDRESS} · ${PHONE} · ${EMAIL}`;

const CONTENT: Record<Lang, PrivacyDoc> = {
  de: {
    title: 'Datenschutzerklärung',
    intro:
      'Wir, SeaDent Mallorca (im Folgenden „wir" oder „SeaDent"), nehmen den Schutz Ihrer personenbezogenen Daten sehr ernst. Diese Datenschutzerklärung informiert Sie darüber, wie wir Ihre Daten erheben, verarbeiten und schützen, wenn Sie unsere Website besuchen oder unsere Dienstleistungen nutzen. Sie entspricht der Datenschutz-Grundverordnung (DSGVO) sowie den geltenden spanischen Datenschutzgesetzen (LOPDGDD).',
    controllerHeading: '1. Verantwortlicher',
    controllerIntro: 'Verantwortlicher im Sinne der DSGVO ist:',
    sections: [
      {
        heading: '2. Erhobene Daten, Zwecke und Rechtsgrundlagen',
        bulletsIntro: 'Wir verarbeiten personenbezogene Daten in folgenden Fällen:',
        bullets: [
          'Nutzungsdaten beim Besuch der Website (IP-Adresse, Browsertyp, Betriebssystem, besuchte Seiten, Datum und Dauer) – Rechtsgrundlage: berechtigtes Interesse am sicheren Betrieb der Website (Art. 6 Abs. 1 lit. f DSGVO) bzw. Ihre Einwilligung bei Analyse-Diensten (Art. 6 Abs. 1 lit. a DSGVO).',
          'Kontaktdaten bei Anfragen über Formular, E-Mail oder Telefon (Name, E-Mail, Telefonnummer, Nachricht) – Rechtsgrundlage: Anbahnung/Erfüllung eines Vertrags oder berechtigtes Interesse (Art. 6 Abs. 1 lit. b und f DSGVO).',
          'Termin- und Behandlungsdaten zur Terminvereinbarung und Behandlung – Rechtsgrundlage: Vertrag (Art. 6 Abs. 1 lit. b DSGVO) sowie Gesundheitsversorgung (Art. 9 Abs. 2 lit. h DSGVO).',
        ],
      },
      {
        heading: '3. Server-Logfiles und Hosting',
        paragraphs: [
          'Beim Aufruf unserer Website werden automatisch Informationen in sogenannten Server-Logfiles erfasst, die Ihr Browser übermittelt (z. B. IP-Adresse, Datum und Uhrzeit, abgerufene Datei). Diese Daten dienen der technischen Bereitstellung, der Sicherheit und der Stabilität der Website und werden nicht mit anderen Datenquellen zusammengeführt.',
        ],
      },
      {
        heading: '4. Cookies und Einwilligung',
        paragraphs: [
          'Unsere Website verwendet Cookies. Notwendige Cookies sind für den Betrieb erforderlich. Analyse- und Marketing-Cookies werden erst gesetzt, nachdem Sie über unser Cookie-Banner ausdrücklich eingewilligt haben (Consent Mode). Ihre Einwilligung können Sie jederzeit über die Cookie-Einstellungen widerrufen oder anpassen.',
        ],
      },
      {
        heading: '5. Google-Dienste',
        bulletsIntro:
          'Sofern Sie eingewilligt haben, nutzen wir Dienste der Google Ireland Limited (Gordon House, Barrow Street, Dublin 4, Irland). Dabei können Daten an Server in den USA übermittelt werden; Google ist unter dem EU-US Data Privacy Framework zertifiziert.',
        bullets: [
          'Google Analytics / Google Tag Manager – Reichweiten- und Nutzungsanalyse (mit IP-Anonymisierung).',
          'Google Maps – Anzeige unseres Standorts.',
          'Google reCAPTCHA – Schutz unserer Formulare vor Missbrauch.',
          'Google Fonts – Darstellung von Schriftarten.',
        ],
      },
      {
        heading: '6. Kontaktformular und Terminbuchung',
        paragraphs: [
          'Wenn Sie uns über das Kontaktformular oder die Online-Terminbuchung kontaktieren, verarbeiten wir die von Ihnen angegebenen Daten zur Bearbeitung Ihrer Anfrage und zur Terminorganisation. Die Daten werden vertraulich behandelt und nicht ohne Ihre Einwilligung an Dritte weitergegeben, soweit dies nicht zur Leistungserbringung erforderlich ist.',
        ],
      },
      {
        heading: '7. Gesundheitsdaten',
        paragraphs: [
          'Im Rahmen zahnärztlicher Behandlungen verarbeiten wir besondere Kategorien personenbezogener Daten (Gesundheitsdaten) gemäß Art. 9 DSGVO. Diese Verarbeitung erfolgt ausschließlich zum Zweck der Gesundheitsversorgung (Art. 9 Abs. 2 lit. h DSGVO) durch ärztliches bzw. zahnärztliches Personal, das der beruflichen Schweigepflicht unterliegt, oder auf Grundlage Ihrer ausdrücklichen Einwilligung.',
        ],
      },
      {
        heading: '8. Datenweitergabe und Drittlandübermittlung',
        bulletsIntro: 'Eine Weitergabe Ihrer Daten erfolgt nur, wenn:',
        bullets: [
          'dies zur Erbringung unserer Leistungen erforderlich ist (z. B. an Dentallabore),',
          'eine gesetzliche Verpflichtung besteht,',
          'Sie ausdrücklich eingewilligt haben.',
          'Bei der Nutzung von Google-Diensten können Daten in die USA übermittelt werden (siehe Abschnitt 5).',
        ],
      },
      {
        heading: '9. Speicherdauer',
        paragraphs: [
          'Wir speichern personenbezogene Daten nur so lange, wie es für die genannten Zwecke erforderlich ist oder gesetzliche Aufbewahrungsfristen (insbesondere für medizinische Dokumentation) dies vorschreiben. Analyse-Daten werden anschließend gelöscht oder anonymisiert.',
        ],
      },
      {
        heading: '10. Ihre Rechte',
        bulletsIntro: 'Nach der DSGVO haben Sie folgende Rechte:',
        bullets: [
          'Auskunft über die zu Ihrer Person gespeicherten Daten (Art. 15),',
          'Berichtigung unrichtiger Daten (Art. 16),',
          'Löschung („Recht auf Vergessenwerden", Art. 17),',
          'Einschränkung der Verarbeitung (Art. 18),',
          'Datenübertragbarkeit (Art. 20),',
          'Widerspruch gegen die Verarbeitung (Art. 21),',
          'Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft (Art. 7 Abs. 3).',
        ],
      },
      {
        heading: '11. Beschwerderecht bei der Aufsichtsbehörde',
        paragraphs: [
          'Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Zuständig in Spanien ist die Agencia Española de Protección de Datos (AEPD), C/ Jorge Juan 6, 28001 Madrid – www.aepd.es. Sie können sich auch an die Aufsichtsbehörde Ihres gewöhnlichen Aufenthaltsorts wenden.',
        ],
      },
      {
        heading: '12. Datensicherheit',
        paragraphs: [
          'Wir setzen technische und organisatorische Maßnahmen ein, um Ihre Daten vor unbefugtem Zugriff, Verlust oder Missbrauch zu schützen, unter anderem eine SSL-/TLS-Verschlüsselung der Website.',
        ],
      },
      {
        heading: '13. Änderungen dieser Datenschutzerklärung',
        paragraphs: [
          'Wir behalten uns vor, diese Datenschutzerklärung anzupassen, etwa bei rechtlichen oder technischen Änderungen. Die jeweils aktuelle Fassung ist auf dieser Website verfügbar.',
        ],
      },
      {
        heading: '14. Kontakt zum Datenschutz',
        paragraphs: [
          `Für Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte erreichen Sie uns unter: ${CONTACT_BLOCK}.`,
        ],
      },
    ],
    status: `Stand: ${LAST_UPDATED}`,
  },
  en: {
    title: 'Privacy Policy',
    intro:
      'We, SeaDent Mallorca (hereinafter “we” or “SeaDent”), take the protection of your personal data very seriously. This privacy policy informs you about how we collect, process, and protect your data when you visit our website or use our services. It complies with the General Data Protection Regulation (GDPR) and applicable Spanish data protection law (LOPDGDD).',
    controllerHeading: '1. Controller',
    controllerIntro: 'The controller within the meaning of the GDPR is:',
    sections: [
      {
        heading: '2. Data Collected, Purposes and Legal Bases',
        bulletsIntro: 'We process personal data in the following cases:',
        bullets: [
          'Usage data when visiting the website (IP address, browser type, operating system, pages visited, date and duration) – legal basis: legitimate interest in the secure operation of the website (Art. 6(1)(f) GDPR), or your consent for analytics services (Art. 6(1)(a) GDPR).',
          'Contact data for inquiries via form, email or phone (name, email, phone number, message) – legal basis: initiation/performance of a contract or legitimate interest (Art. 6(1)(b) and (f) GDPR).',
          'Appointment and treatment data for scheduling and treatment – legal basis: contract (Art. 6(1)(b) GDPR) and provision of health care (Art. 9(2)(h) GDPR).',
        ],
      },
      {
        heading: '3. Server Log Files and Hosting',
        paragraphs: [
          'When you access our website, information is automatically collected in so-called server log files transmitted by your browser (e.g., IP address, date and time, requested file). This data is used for the technical provision, security, and stability of the website and is not merged with other data sources.',
        ],
      },
      {
        heading: '4. Cookies and Consent',
        paragraphs: [
          'Our website uses cookies. Necessary cookies are required for operation. Analytics and marketing cookies are only set after you have given explicit consent via our cookie banner (Consent Mode). You can withdraw or adjust your consent at any time via the cookie settings.',
        ],
      },
      {
        heading: '5. Google Services',
        bulletsIntro:
          'Provided you have given consent, we use services of Google Ireland Limited (Gordon House, Barrow Street, Dublin 4, Ireland). Data may be transferred to servers in the USA; Google is certified under the EU-US Data Privacy Framework.',
        bullets: [
          'Google Analytics / Google Tag Manager – reach and usage analysis (with IP anonymization).',
          'Google Maps – display of our location.',
          'Google reCAPTCHA – protection of our forms against abuse.',
          'Google Fonts – display of fonts.',
        ],
      },
      {
        heading: '6. Contact Form and Appointment Booking',
        paragraphs: [
          'When you contact us via the contact form or online appointment booking, we process the data you provide to handle your request and organize appointments. The data is treated confidentially and not shared with third parties without your consent, unless necessary to provide the service.',
        ],
      },
      {
        heading: '7. Health Data',
        paragraphs: [
          'In the context of dental treatments, we process special categories of personal data (health data) pursuant to Art. 9 GDPR. This processing is carried out exclusively for the purpose of health care (Art. 9(2)(h) GDPR) by medical or dental staff bound by professional secrecy, or on the basis of your explicit consent.',
        ],
      },
      {
        heading: '8. Data Sharing and International Transfers',
        bulletsIntro: 'Your data will only be shared if:',
        bullets: [
          'it is necessary to provide our services (e.g., to dental laboratories),',
          'there is a legal obligation,',
          'you have given explicit consent.',
          'When using Google services, data may be transferred to the USA (see section 5).',
        ],
      },
      {
        heading: '9. Storage Period',
        paragraphs: [
          'We store personal data only as long as necessary for the stated purposes or as required by statutory retention periods (in particular for medical documentation). Analytics data is subsequently deleted or anonymized.',
        ],
      },
      {
        heading: '10. Your Rights',
        bulletsIntro: 'Under the GDPR, you have the following rights:',
        bullets: [
          'Access to the data stored about you (Art. 15),',
          'Rectification of inaccurate data (Art. 16),',
          'Erasure (“right to be forgotten”, Art. 17),',
          'Restriction of processing (Art. 18),',
          'Data portability (Art. 20),',
          'Objection to processing (Art. 21),',
          'Withdrawal of consent with future effect (Art. 7(3)).',
        ],
      },
      {
        heading: '11. Right to Lodge a Complaint',
        paragraphs: [
          'You have the right to lodge a complaint with a data protection supervisory authority. The competent authority in Spain is the Agencia Española de Protección de Datos (AEPD), C/ Jorge Juan 6, 28001 Madrid – www.aepd.es. You may also contact the supervisory authority of your habitual residence.',
        ],
      },
      {
        heading: '12. Data Security',
        paragraphs: [
          'We implement technical and organizational measures to protect your data against unauthorized access, loss, or misuse, including SSL/TLS encryption of the website.',
        ],
      },
      {
        heading: '13. Changes to this Privacy Policy',
        paragraphs: [
          'We reserve the right to adapt this privacy policy, e.g., due to legal or technical changes. The current version is always available on this website.',
        ],
      },
      {
        heading: '14. Privacy Contact',
        paragraphs: [
          `For questions about data protection or to exercise your rights, contact us at: ${CONTACT_BLOCK}.`,
        ],
      },
    ],
    status: `Last updated: ${LAST_UPDATED}`,
  },
  es: {
    title: 'Política de Privacidad',
    intro:
      'Nosotros, SeaDent Mallorca (en adelante, «nosotros» o «SeaDent»), tomamos muy en serio la protección de sus datos personales. Esta política de privacidad le informa sobre cómo recopilamos, tratamos y protegemos sus datos cuando visita nuestro sitio web o utiliza nuestros servicios. Cumple con el Reglamento General de Protección de Datos (RGPD) y la legislación española de protección de datos (LOPDGDD).',
    controllerHeading: '1. Responsable del tratamiento',
    controllerIntro: 'El responsable del tratamiento en el sentido del RGPD es:',
    sections: [
      {
        heading: '2. Datos recopilados, finalidades y bases jurídicas',
        bulletsIntro: 'Tratamos datos personales en los siguientes casos:',
        bullets: [
          'Datos de uso al visitar el sitio web (dirección IP, tipo de navegador, sistema operativo, páginas visitadas, fecha y duración) – base jurídica: interés legítimo en el funcionamiento seguro del sitio (art. 6.1.f RGPD) o su consentimiento para servicios de análisis (art. 6.1.a RGPD).',
          'Datos de contacto en consultas por formulario, correo o teléfono (nombre, correo, teléfono, mensaje) – base jurídica: inicio/ejecución de un contrato o interés legítimo (art. 6.1.b y f RGPD).',
          'Datos de citas y tratamiento para la gestión de citas y la atención – base jurídica: contrato (art. 6.1.b RGPD) y prestación de asistencia sanitaria (art. 9.2.h RGPD).',
        ],
      },
      {
        heading: '3. Archivos de registro del servidor y alojamiento',
        paragraphs: [
          'Al acceder a nuestro sitio web, se recopila automáticamente información en los denominados archivos de registro del servidor que transmite su navegador (p. ej., dirección IP, fecha y hora, archivo solicitado). Estos datos sirven para la prestación técnica, la seguridad y la estabilidad del sitio y no se combinan con otras fuentes de datos.',
        ],
      },
      {
        heading: '4. Cookies y consentimiento',
        paragraphs: [
          'Nuestro sitio web utiliza cookies. Las cookies necesarias son imprescindibles para su funcionamiento. Las cookies de análisis y marketing solo se instalan después de que usted haya dado su consentimiento explícito en nuestro banner de cookies (Modo de consentimiento). Puede retirar o ajustar su consentimiento en cualquier momento a través de la configuración de cookies.',
        ],
      },
      {
        heading: '5. Servicios de Google',
        bulletsIntro:
          'Siempre que haya dado su consentimiento, utilizamos servicios de Google Ireland Limited (Gordon House, Barrow Street, Dublín 4, Irlanda). Los datos pueden transferirse a servidores en EE. UU.; Google está certificado bajo el Marco de Privacidad de Datos UE-EE. UU.',
        bullets: [
          'Google Analytics / Google Tag Manager – análisis de alcance y uso (con anonimización de IP).',
          'Google Maps – visualización de nuestra ubicación.',
          'Google reCAPTCHA – protección de nuestros formularios frente a usos indebidos.',
          'Google Fonts – visualización de fuentes tipográficas.',
        ],
      },
      {
        heading: '6. Formulario de contacto y reserva de citas',
        paragraphs: [
          'Cuando nos contacta a través del formulario de contacto o la reserva de citas en línea, tratamos los datos que nos facilita para gestionar su solicitud y organizar las citas. Los datos se tratan de forma confidencial y no se ceden a terceros sin su consentimiento, salvo que sea necesario para la prestación del servicio.',
        ],
      },
      {
        heading: '7. Datos de salud',
        paragraphs: [
          'En el marco de los tratamientos odontológicos tratamos categorías especiales de datos personales (datos de salud) conforme al art. 9 RGPD. Este tratamiento se realiza exclusivamente con fines de asistencia sanitaria (art. 9.2.h RGPD) por parte de personal médico u odontológico sujeto al secreto profesional, o sobre la base de su consentimiento explícito.',
        ],
      },
      {
        heading: '8. Cesión de datos y transferencias internacionales',
        bulletsIntro: 'Solo cederemos sus datos cuando:',
        bullets: [
          'sea necesario para prestar nuestros servicios (p. ej., a laboratorios dentales),',
          'exista una obligación legal,',
          'usted haya dado su consentimiento explícito.',
          'Al utilizar servicios de Google, los datos pueden transferirse a EE. UU. (véase el apartado 5).',
        ],
      },
      {
        heading: '9. Plazo de conservación',
        paragraphs: [
          'Conservamos los datos personales solo durante el tiempo necesario para las finalidades indicadas o según lo exijan los plazos legales de conservación (en particular para la documentación médica). Los datos de análisis se eliminan o anonimizan posteriormente.',
        ],
      },
      {
        heading: '10. Sus derechos',
        bulletsIntro: 'Según el RGPD, usted tiene los siguientes derechos:',
        bullets: [
          'Acceso a los datos almacenados sobre usted (art. 15),',
          'Rectificación de datos inexactos (art. 16),',
          'Supresión («derecho al olvido», art. 17),',
          'Limitación del tratamiento (art. 18),',
          'Portabilidad de los datos (art. 20),',
          'Oposición al tratamiento (art. 21),',
          'Retirada del consentimiento con efectos futuros (art. 7.3).',
        ],
      },
      {
        heading: '11. Derecho a reclamar ante la autoridad de control',
        paragraphs: [
          'Tiene derecho a presentar una reclamación ante una autoridad de control de protección de datos. La autoridad competente en España es la Agencia Española de Protección de Datos (AEPD), C/ Jorge Juan 6, 28001 Madrid – www.aepd.es. También puede dirigirse a la autoridad de control de su lugar de residencia habitual.',
        ],
      },
      {
        heading: '12. Seguridad de los datos',
        paragraphs: [
          'Aplicamos medidas técnicas y organizativas para proteger sus datos frente a accesos no autorizados, pérdidas o usos indebidos, incluido el cifrado SSL/TLS del sitio web.',
        ],
      },
      {
        heading: '13. Cambios en esta política de privacidad',
        paragraphs: [
          'Nos reservamos el derecho de adaptar esta política de privacidad, p. ej., por cambios legales o técnicos. La versión actual está siempre disponible en este sitio web.',
        ],
      },
      {
        heading: '14. Contacto sobre protección de datos',
        paragraphs: [
          `Para cualquier consulta sobre protección de datos o para ejercer sus derechos, contáctenos en: ${CONTACT_BLOCK}.`,
        ],
      },
    ],
    status: `Última actualización: ${LAST_UPDATED}`,
  },
  ru: {
    title: 'Политика конфиденциальности',
    intro:
      'Мы, SeaDent Mallorca (далее «мы» или «SeaDent»), очень серьёзно относимся к защите ваших персональных данных. Настоящая политика конфиденциальности информирует вас о том, как мы собираем, обрабатываем и защищаем ваши данные при посещении нашего сайта или использовании наших услуг. Она соответствует Общему регламенту по защите данных (GDPR) и действующему законодательству Испании о защите данных (LOPDGDD).',
    controllerHeading: '1. Ответственное лицо',
    controllerIntro: 'Ответственным лицом по смыслу GDPR является:',
    sections: [
      {
        heading: '2. Собираемые данные, цели и правовые основания',
        bulletsIntro: 'Мы обрабатываем персональные данные в следующих случаях:',
        bullets: [
          'Данные об использовании при посещении сайта (IP-адрес, тип браузера, операционная система, посещённые страницы, дата и продолжительность) — правовое основание: законный интерес в безопасной работе сайта (ст. 6(1)(f) GDPR) либо ваше согласие для аналитических сервисов (ст. 6(1)(a) GDPR).',
          'Контактные данные при обращениях через форму, электронную почту или телефон (имя, эл. почта, телефон, сообщение) — правовое основание: заключение/исполнение договора или законный интерес (ст. 6(1)(b) и (f) GDPR).',
          'Данные о записи и лечении для организации приёма и оказания услуг — правовое основание: договор (ст. 6(1)(b) GDPR) и оказание медицинской помощи (ст. 9(2)(h) GDPR).',
        ],
      },
      {
        heading: '3. Файлы журнала сервера и хостинг',
        paragraphs: [
          'При обращении к нашему сайту информация автоматически фиксируется в так называемых файлах журнала сервера, которые передаёт ваш браузер (например, IP-адрес, дата и время, запрошенный файл). Эти данные используются для технического предоставления, безопасности и стабильности сайта и не объединяются с другими источниками данных.',
        ],
      },
      {
        heading: '4. Файлы cookie и согласие',
        paragraphs: [
          'Наш сайт использует файлы cookie. Необходимые cookie требуются для работы сайта. Аналитические и маркетинговые cookie устанавливаются только после вашего явного согласия через баннер cookie (режим согласия). Вы можете отозвать или изменить согласие в любое время через настройки cookie.',
        ],
      },
      {
        heading: '5. Сервисы Google',
        bulletsIntro:
          'При наличии вашего согласия мы используем сервисы Google Ireland Limited (Gordon House, Barrow Street, Dublin 4, Ирландия). Данные могут передаваться на серверы в США; Google сертифицирован в рамках EU-US Data Privacy Framework.',
        bullets: [
          'Google Analytics / Google Tag Manager — анализ охвата и использования (с анонимизацией IP).',
          'Google Maps — отображение нашего местоположения.',
          'Google reCAPTCHA — защита наших форм от злоупотреблений.',
          'Google Fonts — отображение шрифтов.',
        ],
      },
      {
        heading: '6. Контактная форма и онлайн-запись',
        paragraphs: [
          'Когда вы связываетесь с нами через контактную форму или онлайн-запись, мы обрабатываем предоставленные вами данные для обработки запроса и организации приёма. Данные обрабатываются конфиденциально и не передаются третьим лицам без вашего согласия, если это не требуется для оказания услуги.',
        ],
      },
      {
        heading: '7. Данные о состоянии здоровья',
        paragraphs: [
          'В рамках стоматологического лечения мы обрабатываем особые категории персональных данных (данные о здоровье) в соответствии со ст. 9 GDPR. Эта обработка осуществляется исключительно в целях оказания медицинской помощи (ст. 9(2)(h) GDPR) медицинским или стоматологическим персоналом, обязанным соблюдать профессиональную тайну, либо на основании вашего явного согласия.',
        ],
      },
      {
        heading: '8. Передача данных и международные трансферы',
        bulletsIntro: 'Мы передаём ваши данные только если:',
        bullets: [
          'это необходимо для оказания наших услуг (например, зуботехническим лабораториям),',
          'существует юридическая обязанность,',
          'вы дали явное согласие.',
          'При использовании сервисов Google данные могут передаваться в США (см. раздел 5).',
        ],
      },
      {
        heading: '9. Срок хранения',
        paragraphs: [
          'Мы храним персональные данные только в течение срока, необходимого для указанных целей, или в соответствии с установленными законом сроками хранения (в частности, для медицинской документации). Аналитические данные затем удаляются или анонимизируются.',
        ],
      },
      {
        heading: '10. Ваши права',
        bulletsIntro: 'В соответствии с GDPR вы имеете следующие права:',
        bullets: [
          'доступ к хранящимся о вас данным (ст. 15),',
          'исправление неточных данных (ст. 16),',
          'удаление («право быть забытым», ст. 17),',
          'ограничение обработки (ст. 18),',
          'переносимость данных (ст. 20),',
          'возражение против обработки (ст. 21),',
          'отзыв согласия с действием на будущее (ст. 7(3)).',
        ],
      },
      {
        heading: '11. Право на подачу жалобы',
        paragraphs: [
          'Вы имеете право подать жалобу в надзорный орган по защите данных. Компетентным органом в Испании является Agencia Española de Protección de Datos (AEPD), C/ Jorge Juan 6, 28001 Madrid – www.aepd.es. Вы также можете обратиться в надзорный орган по месту вашего обычного проживания.',
        ],
      },
      {
        heading: '12. Безопасность данных',
        paragraphs: [
          'Мы применяем технические и организационные меры для защиты ваших данных от несанкционированного доступа, потери или злоупотребления, включая SSL/TLS-шифрование сайта.',
        ],
      },
      {
        heading: '13. Изменения настоящей политики',
        paragraphs: [
          'Мы оставляем за собой право изменять настоящую политику конфиденциальности, например, в связи с правовыми или техническими изменениями. Актуальная версия всегда доступна на этом сайте.',
        ],
      },
      {
        heading: '14. Контакт по вопросам защиты данных',
        paragraphs: [
          `По вопросам защиты данных или для реализации ваших прав свяжитесь с нами: ${CONTACT_BLOCK}.`,
        ],
      },
    ],
    status: `Последнее обновление: ${LAST_UPDATED}`,
  },
};

const PrivacyPage: React.FC = () => {
  const { language } = useLanguage();
  const doc = CONTENT[(language as Lang)] ?? CONTENT.de;

  return (
    <div className="container mx-auto px-4 pt-20 lg:pt-32 pb-8 max-w-4xl">
      <Seo pageKey="privacy" />
      <h1 className="text-3xl font-bold text-center mb-8">{doc.title}</h1>

      <div className="prose max-w-none">
        <p className="mb-8">{doc.intro}</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{doc.controllerHeading}</h2>
          <p className="mb-1">{doc.controllerIntro}</p>
          <p className="mb-1">{OWNER}</p>
          <p className="mb-1">{PROVIDER}</p>
          <p className="mb-1">{ADDRESS}</p>
          <p className="mb-1">Tel.: {PHONE}</p>
          <p className="mb-1">E-Mail: {EMAIL}</p>
        </section>

        {doc.sections.map((s, i) => (
          <section className="mb-8" key={i}>
            <h2 className="text-xl font-semibold mb-3">{s.heading}</h2>
            {s.paragraphs?.map((p, j) => (
              <p className="mb-2" key={j}>{p}</p>
            ))}
            {s.bulletsIntro && <p className="mb-2">{s.bulletsIntro}</p>}
            {s.bullets && (
              <ul className="list-disc ml-6 mb-2">
                {s.bullets.map((b, j) => (
                  <li key={j}>{b}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <p className="text-sm text-gray-500 mt-10">{doc.status}</p>
      </div>
    </div>
  );
};

export default PrivacyPage;
